import { type ReactNode } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LegalLinks } from '../legal/LegalLinks';
import logoDark from '../../assets/logo-dark.png';
import logoLight from '../../assets/logo-light.png';
import './AuthLayout.css';

export interface AuthLayoutProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  /** `default` ≈ 28rem (forms) · `wide` ≈ 48rem (reading pages such as the legal documents). */
  width?: 'default' | 'wide';
}

export function AuthLayout({ children, title, subtitle, width = 'default' }: AuthLayoutProps) {
  const { theme } = useTheme();
  const logoSrc = theme === 'dark' ? logoDark : logoLight;

  return (
    <div className="auth-layout" data-testid="auth-layout">
      {/* Language switcher — top-right, all supported locales (banner landmark) */}
      <header className="auth-layout__lang" data-testid="auth-layout-lang">
        <LanguageSwitcher hideLabel />
      </header>

      <main className="auth-layout__main">
      {/* Brand mark */}
      <div className="auth-layout__brand">
        <img
          src={logoSrc}
          alt="reef guide.app"
          className="auth-layout__logo"
        />
      </div>

      {/* Card */}
      <div
        className={width === 'wide' ? 'auth-layout__card auth-layout__card--wide' : 'auth-layout__card'}
        data-testid="auth-layout-card"
      >
        {(title || subtitle) && (
          <div className="auth-layout__header">
            {title && (
              <Heading level={1} className="auth-layout__title" data-testid="auth-layout-title">
                {title}
              </Heading>
            )}
            {subtitle && (
              <Text className="auth-layout__subtitle" data-testid="auth-layout-subtitle">
                {subtitle}
              </Text>
            )}
          </div>
        )}
        <div className="auth-layout__body" data-testid="auth-layout-content">
          {children}
        </div>
      </div>
      </main>

      {/* Imprint · Privacy · Terms — reachable from every public page (§ 5 DDG) */}
      <footer className="auth-layout__footer" data-testid="auth-layout-footer">
        <LegalLinks />
      </footer>
    </div>
  );
}
